import React, { forwardRef, useEffect, useState } from 'react';
import {
  Control,
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
} from 'react-hook-form';
import { GroupBase } from 'react-select';
import { generateRandomString } from 'tradevan-ui/consts';
import {
  ReactSelectProps as TvReactSelectProps,
  ReactSelectRef,
} from 'tradevan-ui/ReactSelect';
import { Ioption } from 'tradevan-ui/type';
import MultiSelectComponent from './components/MultiSelectComponent';

export interface ReactSelectProps<
  T extends Ioption,
  IsMulti extends boolean = false,
  Group extends GroupBase<T> = GroupBase<T>,
  K extends FieldValues = FieldValues,
> extends Omit<TvReactSelectProps<T, IsMulti, Group>, 'name'> {
  control: Control<K> | unknown; // 無法正確判斷 geneic，先設定 unknown
  name: FieldPath<K>;
  rules?: ControllerProps<K, FieldPath<K>>['rules'];
  /** hook-form 必須啟用監聽，value 才會更新 */
  watchValue: () => void;
  /** hook-form 必須要知道 value，才能在指定位置加上 className: "active" */
  value: string;
}

/** react-MultiSelect */
const MultiSelect = forwardRef(
  <
    T extends Ioption,
    IsMulti extends boolean = false,
    Group extends GroupBase<T> = GroupBase<T>,
    K extends FieldValues = FieldValues,
  >(
    props: ReactSelectProps<T, IsMulti, Group, K>,
    ref: ReactSelectRef<T, IsMulti, Group>,
  ): JSX.Element => {
    const { control, name, rules, value, onChange, watchValue, ...other } =
      props;

    watchValue();

    // 外面reset的時候，不加key畫面不會刷新
    const [randomKey, setRandomKey] = useState(generateRandomString(10));

    useEffect(() => {
      if (value.length === 0) {
        setRandomKey(generateRandomString(10));
      }
    }, [value]);

    return (
      <Controller
        key={randomKey}
        control={control as Control<K>}
        name={name}
        rules={rules}
        render={({ field }) => (
          <MultiSelectComponent
            isMulti
            {...field}
            styles={{
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              control: (provider) => ({
                ...provider,
                borderWidth: '1px',
              }),
            }}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            ref={ref}
            value={value}
            onChange={(newValue, actionMeta) => {
              field.onChange(newValue);
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              if (onChange) onChange(newValue, actionMeta);
            }}
            {...other}
          />
        )}
      />
    );
  },
);

MultiSelect.displayName = 'MultiSelect';
export default MultiSelect;
